"use client";

import React, { type ReactNode } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowRight, Building2 } from "lucide-react";
import Reveal from "@/components/common/Reveal";
import { useLanguage } from "@/hooks/useLanguage";
import { pick } from "@/lib/i18n";
import { siteImage } from "@/lib/siteImages";
interface GlanceRow {
  label: string;
  value: string;
}
interface MissionContent {
  badge: string;
  headline: string;
  paragraph1: ReactNode;
  paragraph2: string;
  ctaButton: string;
  glanceTitle: string;
  facilityAlt: string;
  glanceRows: GlanceRow[];
}
const missionData: Record<"en" | "zh" | "es" | "de" | "fr" | "pt", MissionContent> = {
  en: {
    badge: "Company Overview",
    headline: "Who We Are",
    paragraph1: <>
        MutualFilter is the international filtration and separation brand of Hebei Hutuo Wire Mesh Products Co., Ltd., based in Hebei, China. Hebei Hutuo operates two specialized B2B brands, MutualFilter and MutualMetal.<br />We supply woven wire mesh, sintered wire mesh, filter components and wedge wire screens for industrial filtration, screening and separation applications.
      </>,
    paragraph2: "We support industrial buyers, engineers, contractors, distributors, OEMs and project teams with integrated sourcing, specification matching and technical guidance across international markets.",
    ctaButton: "Explore Products",
    glanceTitle: "Company at a Glance",
    facilityAlt: "Cleanroom weaving looms and wire mesh manufacturing line in Anping",
    glanceRows: [{
      label: "Legal entity",
      value: "Hebei Hutuo Wire Mesh Products Co., Ltd."
    }, {
      label: "Brands",
      value: "MutualFilter · MutualMetal"
    }, {
      label: "Manufacturing base",
      value: "Anping, Hebei, China"
    }, {
      label: "Reference standards",
      value: "ISO 9044 / ASTM E2016"
    }]
  },
  zh: {
    badge: "公司概况",
    headline: "我们是谁",
    paragraph1: <>
        MutualFilter 是河北互拓丝网制品有限公司旗下的国际过滤与分离品牌，总部位于中国河北。河北互拓运营 MutualFilter 和 MutualMetal 两个专业 B2B 品牌。<br />我们提供编织丝网、烧结丝网、过滤组件及楔形滤网，应用于工业过滤、筛分与分离场景。
      </>,
    paragraph2: "我们通过整合的产品采购、规格匹配与技术指导，为工业买家、工程师、承包商、经销商、OEM 厂商及项目团队提供支持，服务覆盖国际市场。",
    ctaButton: "探索产品",
    glanceTitle: "企业概况速览",
    facilityAlt: "位于安平的不锈钢丝网织机车间及精密制造生产线",
    glanceRows: [{
      label: "法定主体",
      value: "河北互拓丝网制品有限公司"
    }, {
      label: "旗下品牌",
      value: "MutualFilter · MutualMetal"
    }, {
      label: "制造基地",
      value: "中国河北安平"
    }, {
      label: "参考标准",
      value: "ISO 9044 / ASTM E2016"
    }]
  },
  es: {
    badge: "Perfil de la empresa",
    headline: "Quiénes somos",
    paragraph1: <>
        MutualFilter es la marca internacional de filtración y separación de Hebei Hutuo Wire Mesh Products Co., Ltd., con sede en Hebei, China. Hebei Hutuo opera dos marcas B2B especializadas: MutualFilter y MutualMetal.<br />Suministramos malla de alambre tejida, malla sinterizada, componentes de filtración y tamices de alambre en cuña para aplicaciones de filtración, cribado y separación industrial.
      </>,
    paragraph2: "Respaldamos a compradores industriales, ingenieros, contratistas, distribuidores, fabricantes de equipo original (OEM) y equipos de proyecto con aprovisionamiento integrado, ajuste de especificaciones y asesoría técnica en mercados internacionales.",
    ctaButton: "Explorar productos",
    glanceTitle: "La empresa de un vistazo",
    facilityAlt: "Telares industriales de precisión y línea de fabricación de malla metálica en Anping",
    glanceRows: [{
      label: "Entidad jurídica",
      value: "Hebei Hutuo Wire Mesh Products Co., Ltd."
    }, {
      label: "Marcas",
      value: "MutualFilter · MutualMetal"
    }, {
      label: "Base de fabricación",
      value: "Anping, Hebei, China"
    }, {
      label: "Normas de referencia",
      value: "ISO 9044 / ASTM E2016"
    }]
  },
  de: {
    badge: "Unternehmensübersicht",
    headline: "Wer wir sind",
    paragraph1: <>
        MutualFilter ist die internationale Marke für Filtration und Trenntechnik von Hebei Hutuo Wire Mesh Products Co., Ltd. mit Sitz in Hebei, China. Hebei Hutuo führt zwei spezialisierte B2B-Marken: MutualFilter und MutualMetal.<br />Wir liefern Drahtgewebe, Sinterdrahtgewebe, Filterkomponenten und Spaltsiebe für industrielle Filtrations-, Sieb- und Trennprozesse.
      </>,
    paragraph2: "Wir unterstützen industrielle Einkäufer, Ingenieure, Anlagenbauer, Distributoren, OEMs und Projektteams mit integrierter Beschaffung, Spezifikationsabgleich und technischer Beratung auf internationalen Märkten.",
    ctaButton: "Produkte entdecken",
    glanceTitle: "Unternehmen im Überblick",
    facilityAlt: "Präzisionswebstühle und Fertigungsanlagen für Drahtgewebe in Anping",
    glanceRows: [{
      label: "Rechtsträger",
      value: "Hebei Hutuo Wire Mesh Products Co., Ltd."
    }, {
      label: "Marken",
      value: "MutualFilter · MutualMetal"
    }, {
      label: "Produktionsstandort",
      value: "Anping, Hebei, China"
    }, {
      label: "Referenzstandards",
      value: "ISO 9044 / ASTM E2016"
    }]
  },
  fr: {
    badge: "Présentation de l'entreprise",
    headline: "Qui sommes-nous",
    paragraph1: <>
        MutualFilter est la marque internationale de filtration et de séparation de Hebei Hutuo Wire Mesh Products Co., Ltd., basée dans le Hebei, en Chine. Hebei Hutuo exploite deux marques B2B spécialisées : MutualFilter et MutualMetal.<br />Nous fournissons des toiles métalliques tissées, des toiles métalliques frittées, des composants de filtration et des crépines à fil profilé pour les applications industrielles de filtration, tamisage et séparation.
      </>,
    paragraph2: "Nous accompagnons les acheteurs industriels, ingénieurs, contractants, distributeurs, équipementiers (OEM) et équipes de projet grâce à un approvisionnement intégré, une adéquation précise des spécifications et des conseils techniques sur les marchés internationaux.",
    ctaButton: "Découvrir les produits",
    glanceTitle: "L'entreprise en un coup d'œil",
    facilityAlt: "Métiers à tisser de précision et ligne de production de toiles métalliques à Anping",
    glanceRows: [{
      label: "Entité juridique",
      value: "Hebei Hutuo Wire Mesh Products Co., Ltd."
    }, {
      label: "Marques",
      value: "MutualFilter · MutualMetal"
    }, {
      label: "Site de fabrication",
      value: "Anping, Hebei, Chine"
    }, {
      label: "Normes de référence",
      value: "ISO 9044 / ASTM E2016"
    }]
  },
  pt: {
    badge: "Visão geral da empresa",
    headline: "Quem somos",
    paragraph1: <>
        MutualFilter é a marca internacional de filtração e separação da Hebei Hutuo Wire Mesh Products Co., Ltd., com sede em Hebei, China. A Hebei Hutuo opera duas marcas B2B especializadas: MutualFilter e MutualMetal.<br />Fornecemos malha de arame tecida, malha sinterizada, componentes de filtração e telas de arame em cunha para aplicações de filtração, peneiramento e separação industrial.
      </>,
    paragraph2: "Apoiamos compradores industriais, engenheiros, empreiteiros, distribuidores, OEMs e equipes de projeto com fornecimento integrado, adequação de especificações e suporte técnico nos mercados internacionais.",
    ctaButton: "Explorar produtos",
    glanceTitle: "A empresa em resumo",
    facilityAlt: "Tares industriais de precisão e linha de fabricação de malhas metálicas em Anping",
    glanceRows: [{
      label: "Entidade jurídica",
      value: "Hebei Hutuo Wire Mesh Products Co., Ltd."
    }, {
      label: "Marcas",
      value: "MutualFilter · MutualMetal"
    }, {
      label: "Base de fabricação",
      value: "Anping, Hebei, China"
    }, {
      label: "Normas de referência",
      value: "ISO 9044 / ASTM E2016"
    }]
  }
};
export default function Mission() {
  const {
    lang
  } = useLanguage();
  const content = pick(lang, missionData);
  return <section id="companyoverview" data-nav="light" className="w-full bg-background border-b border-border mf-section">
      <div className="mf-container">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Left Column (7 cols on lg) */}
          <div className="lg:col-span-7 flex flex-col items-start space-y-5">
            <Reveal>
              <div className="inline-flex items-center gap-2 font-mono mf-caption uppercase tracking-wider text-foreground font-semibold px-2.5 py-1 rounded-md bg-muted border border-border">
                <Building2 className="size-3.5 text-primary" />
                <span>{content.badge}</span>
              </div>
            </Reveal>

            <Reveal delay={60}>
              <h2 className="mf-h2 text-foreground font-default">
                {content.headline}
              </h2>
            </Reveal>

            <Reveal delay={120}>
              <div className="space-y-4">
                <p className="mf-lead text-muted-foreground leading-relaxed">
                  {content.paragraph1}
                </p>
                <p className="mf-lead text-muted-foreground leading-relaxed">
                  {content.paragraph2}
                </p>
              </div>
            </Reveal>

            <Reveal delay={180}>
              <div className="pt-2">
                <Button asChild size="lg" className="rounded-md">
                  <Link to="/ProductsPage" className="inline-flex items-center gap-2 font-medium">
                    <span>{content.ctaButton}</span>
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
              </div>
            </Reveal>
          </div>

          {/* Right Column: Company at a glance Card with Facility Photo (5 cols on lg) */}
          <div className="lg:col-span-5 w-full">
            <Reveal delay={100}>
              <Card className="rounded-lg border border-border bg-card overflow-hidden gap-0 py-0 mf-hover-lift">
                {/* Clean industrial facility preview image */}
                <div className="mf-card-media mf-ratio-card">
                  <img src={siteImage("7c6fe4-anping-mesh-manufactu-cf7f67.webp")} alt={content.facilityAlt} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                  <div className="absolute bottom-2.5 left-3 font-mono mf-caption uppercase tracking-wider text-foreground bg-card px-2 py-0.5 rounded border border-border">
                    Anping, Hebei
                  </div>
                </div>

                <div className="p-6 sm:p-7">
                  <h3 className="mf-h3 text-foreground font-semibold mb-5">
                    {content.glanceTitle}
                  </h3>

                  <dl className="divide-y divide-border">
                    {content.glanceRows.map((row, idx) => <div key={idx} data-index={idx} className="py-3 first:pt-0 last:pb-0 flex flex-col gap-1">
                        <dt className="mf-caption font-mono uppercase tracking-wider text-muted-foreground">
                          {row.label}
                        </dt>
                        <dd className="text-sm sm:text-base font-medium text-foreground leading-snug">
                          {row.value}
                        </dd>
                      </div>)}
                  </dl>
                </div>
              </Card>
            </Reveal>
          </div>
        </div>
      </div>
    </section>;
}